import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useTranslation } from "@/core/i18n";

import OnboardingHeaderV3 from "../OnboardingScreenHeader/OnboardingHeaderV3";

interface VerificationCodeFormProps {
  phoneNumber?: string;
  onConfirm?: (code: string) => Promise<void>;
  onResendCode?: () => Promise<void>;
  resendCooldownSeconds?: number;
  className?: string;
}

export const VerificationCodeForm = ({
  phoneNumber,
  onConfirm,
  onResendCode,
  resendCooldownSeconds = 60,
  className = "",
}: VerificationCodeFormProps) => {
  const { t } = useTranslation();
  const [value, setCodevalue] = useState("");
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmationError, setConfirmationError] = useState<string | undefined>(undefined);
  const [resendError, setResendError] = useState<string | undefined>(undefined);
  const [resendCountdown, setResendCountdown] = useState(resendCooldownSeconds);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastAutoSubmittedCodeRef = useRef<string | null>(null);
  const isSubmittingRef = useRef<boolean>(false);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCountdown > 0) {
      countdownIntervalRef.current = setInterval(() => {
        setResendCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
    };
  }, [resendCountdown]);

  const handleConfirm = useCallback(async () => {
    if (value.length !== 6 || !onConfirm) return;
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setConfirmationError(undefined);
    try {
      await onConfirm(value);
    } catch (error) {
      setConfirmationError(
        error instanceof Error ? error.message : t("verificationCodeForm.confirmError"),
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  }, [value, onConfirm, t]);

  // Auto-submit when all digits are filled
  useEffect(() => {
    if (!onConfirm) return;
    if (value.length !== 6) return;
    if (isSubmittingRef.current) return;
    if (lastAutoSubmittedCodeRef.current === value) return;
    lastAutoSubmittedCodeRef.current = value;
    void handleConfirm();
  }, [value, onConfirm, handleConfirm]);

  const handleResend = async () => {
    if (isSubmitting || isResendLoading || resendCountdown > 0 || !onResendCode) return;
    setIsResendLoading(true);
    setResendError(undefined);

    try {
      await onResendCode();
    } catch (error) {
      setResendError(
        error instanceof Error ? error.message : t("verificationCodeForm.resendError"),
      );
    } finally {
      setIsResendLoading(false);
      // Always start countdown, even on failure
      setResendCountdown(resendCooldownSeconds);
    }
  };

  // Cleanup countdown interval on unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, []);

  const slotClassName = `
    h-12 w-10 rounded-[4px] border border-solid bg-white font-sans
    text-base font-normal leading-[normal] text-black shadow-none
    focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-50
  `;

  const maskedPhoneNumber = useMemo(() => {
    if (!phoneNumber) return t("verificationCodeForm.yourPhoneNumberFallback");
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    const lastFour = (digitsOnly || phoneNumber).slice(-4);
    return `****${lastFour}`;
  }, [phoneNumber, t]);

  return (
    <div className={`flex w-full max-w-sm flex-col items-center gap-9 ${className}`}>
      <OnboardingHeaderV3
        title={t("verificationCodeForm.title")}
        subtitle={t("verificationCodeForm.subtitle", { maskedPhoneNumber })}
        align="left"
      />

      <InputOTP
        maxLength={6}
        value={value}
        onChange={setCodevalue}
        containerClassName="gap-2"
        disabled={isSubmitting}
        autoFocus
        inputMode="numeric"
        autoComplete="one-time-code"
      >
        <InputOTPGroup className="gap-2">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <InputOTPSlot
              key={index}
              index={index}
              className={slotClassName}
              hasError={!!confirmationError}
              inactiveBorderColor="border-white"
              activeBorderColor="border-brand-color"
            />
          ))}
        </InputOTPGroup>
      </InputOTP>

      {/* Spinner appears below inputs when submitting, replaces the button */}
      {isSubmitting ? (
        <div className="flex h-5 w-full items-center justify-center">
          <div className="size-5 animate-spin rounded-full border-2 border-brand-color border-b-transparent" />
        </div>
      ) : null}

      {confirmationError && <div className="text-center text-sm text-red">{confirmationError}</div>}

      {/* Confirm button - matches Figma with 20% opacity when disabled */}
      <button
        disabled={value.length < 6 || isSubmitting}
        onClick={handleConfirm}
        className="w-[326px] rounded-[67px] bg-brand-color px-6 py-[13px] font-sans text-body1 font-normal text-white transition-opacity disabled:opacity-20"
        style={{ fontVariationSettings: "'opsz' 14" }}
      >
        {t("common.confirm")}
      </button>

      {/* Resend code button - only show when not submitting */}
      {!isSubmitting && (
        <div className="flex w-[274px] flex-col items-center">
          {isResendLoading ? (
            <div className="size-5 animate-spin rounded-full border-2 border-light-grey border-t-brand-color" />
          ) : resendCountdown > 0 ? (
            <span
              className="text-center font-sans text-[14px] font-medium leading-[18px] text-secondary"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              {t("verificationCodeForm.resendIn", { seconds: resendCountdown })}
            </span>
          ) : (
            <button
              onClick={handleResend}
              className="text-center font-sans text-[14px] font-medium leading-[18px] text-brand-color transition-opacity hover:opacity-80"
              style={{ fontVariationSettings: "'opsz' 14" }}
            >
              {t("verificationCodeForm.resendCode")}
            </button>
          )}

          {resendError && <div className="mt-2 text-center text-sm text-red">{resendError}</div>}
        </div>
      )}
    </div>
  );
};
